// React Dependencies
import React, { useState } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Chip, makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { toast } from 'react-toastify';
import classNames from 'classnames';

// Internal Dependencies
import { EscapeSingleQuote, formatAddresses, StringToLowercase } from '../../../common/util';
import { IsEmpty, IsString } from '../../../common/checks';
import Constants from '../../../common/constants';
import Colours from '../../../styles/colours';
import apiService from '../../../services.js/apiService';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiInputBase-root': {
      alignItems: 'start',
    },
  },
  green: {
    backgroundColor: '#2ecc71',
    color:           '#FFFFFF',
  },
  red: {
    backgroundColor: '#e74c3c',
    color:           '#FFFFFF',
  },
}));

const PartnerBuildingChips = ({ addedAddresses, setAddedAddresses }) => {
  const classes = useStyles();
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);

  const findAndRemoveAddress = (values, addressName = '') => {
    // user deleted one of the partner. Find out which is deleted
    const existingAddresses = _.cloneDeep(addedAddresses);

    for (const index in existingAddresses) {
      if (!IsEmpty(index)) {
        let deleted = true;
        if (addressName) {
          deleted = addressName === existingAddresses[index];
        } else {
          for (const value of values) {
            if (value.name === existingAddresses[index]) {
              deleted = false;
              break;
            }
          }
        }
        if (deleted) {
          // found the partner index to delete
          existingAddresses.splice(index, 1);
          break;
        }
      }
    }

    // Code below is to update items displayed in the UI
    const existingAddedAddresses = _.cloneDeep(addedAddresses);
    for (const index in existingAddedAddresses) {
      if (!IsEmpty(index)) {
        let deleted = true;
        if (addressName) {
          deleted = addressName === existingAddedAddresses[index].name;
        } else {
          for (const value of values) {
            if (value.name === existingAddedAddresses[index].name) {
              deleted = false;
              break;
            }
          }
        }
        if (deleted) {
          // found the index to delete
          existingAddedAddresses.splice(index, 1);
          break;
        }
      }
    }

    setAddedAddresses(existingAddedAddresses);
  };

  const handleDelete = (value) => {
    try {
      findAndRemoveAddress([], value.name);
    } catch (err) {
      toast.error(Constants.Error.ReportToIT);
      toast.error(err);
    }
  };

  const addValueIfNew = async (value) => {
    try {
      let newValue;

      // handle case where user hit 'Enter', which causes input 'value' to be a string
      if (IsString(value)) {
        const addressFound = _.find(suggestedAddresses, (address) => StringToLowercase(address.name) === StringToLowercase(value));

        if (_.isEmpty(addressFound)) {
          return toast.warn('Please select the value from dropdown list, new custom value does not exist in addresses');
        } else {
          newValue = addressFound;
        }
      } else {
        newValue = value;
      }

      const addressAlreadyExist = _.some(addedAddresses, (address) => address.name === newValue.name);

      if (!addressAlreadyExist) {
        setAddedAddresses((prevAddresses) => [...prevAddresses, newValue]);
      }
    } catch (error) {
      toast.error('Error in addValueIfNew(): ', error);
    }
  };

  const handleChanges = (values) => {
    try {
      // Note that the data is an array of { name, postalCodes, hasPostalCode }
      if (IsEmpty(values)) {
        setAddedAddresses([]);
      } else if (values.length > addedAddresses.length) {
        // new value added will always be the last time
        addValueIfNew(values[values.length - 1]);
      } else {
        findAndRemoveAddress(values);
      }
    } catch (err) {
      toast.error(Constants.Error.ReportToIT);
      toast.error(err);
    }
  };

  const onInputChanged = async (value) => {
    try {
      const nameSearch = EscapeSingleQuote(value);

      if (!IsEmpty(nameSearch)) {
        const { rows } = await apiService.getAddresses({ name: nameSearch });

        // format the address, the data returned is an array of { name, postalCodes, hasPostalCode }
        const formattedAddresses = formatAddresses(rows);

        // xorBy, results with only those records whose "name" is not the same in both array
        const uniqueAddresses = _.xorBy(formattedAddresses, addedAddresses, 'name');

        setSuggestedAddresses(uniqueAddresses || []);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const inputDebounced = _.debounce(onInputChanged, 500);

  return (
    <div className="d-flex flex-column">
      <Autocomplete
        className={classes.root}
        multiple
        options={suggestedAddresses}
        getOptionLabel={(option) => option.name}
        value={addedAddresses}
        disableCloseOnSelect
        filterSelectedOptions
        freeSolo
        renderOption={(option) => (
          <>
            <span
              classes={{
                root: classNames({
                  [classes.green]: option.hasPostalCode === true,
                  [classes.red]:   option.hasPostalCode === false,
                }),
              }}
              style={{
                background:
                  option.hasPostalCode === true ? Colours.Green
                    : option.hasPostalCode === false ? Colours.Red
                      : Colours.Gray1,
                color:        Colours.White,
                marginRight:  15,
                width:        10,
                height:       10,
                borderRadius: 10,
              }}
            />
            {option.name}
          </>
        )}
        renderTags={(value, getTagProps) => value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            variant="outlined"
            label={option.name}
            classes={{
              root: classNames({
                [classes.green]: option.hasPostalCode === true,
                [classes.red]:   option.hasPostalCode === false,
              }),
            }}
            style={{ borderColor: Colours.White, fontSize: 14, cursor: 'pointer' }}
            onDelete={() => handleDelete(option)}
          />
        ))}
        onChange={(event, values) => handleChanges(values)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            onChange={(e) => inputDebounced(e.target.value)}
            placeholder="Type and hit 'Enter' to add"
            multiline
            rows={5}
          />
        )}
      />
    </div>
  );
};

export default PartnerBuildingChips;

PartnerBuildingChips.propTypes = {
  addedAddresses:    PropTypes.arrayOf(PropTypes.shape(Object)).isRequired,
  setAddedAddresses: PropTypes.func.isRequired,
};
